import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, HelperText, Input, InputRow, Textarea } from '@/ui';
import { useActiveDiligencias, useCasesPick, useRecentTasks } from '@/hooks';
import { toISODate } from '@/lib';
import { isValidDateInput, MAX_DATE_INPUT } from '@/lib/dateUtils';
import { admitsDiligencia } from '@/pages/causas/causaDisplay';

import './NuevaDiligenciaForm.css';
import {
  ADDRESS_AUTOCOMPLETE_PROVIDER,
  ADDRESS_AUTOCOMPLETE_KEY,
} from '@/app/config';
import AddressAutocomplete from './direccion/AddressAutocomplete';
import { getAddressAdapter, proposeComuna } from './direccion/adapters';
import ActuacionFields, { AddressTypePicker } from './ActuacionFields';
import ComunaCombo from './ComunaCombo';
import { COMUNAS, resolveComuna } from './comunas';
import {
  actuacionFromTask,
  actuacionPayload,
  validateActuacion,
} from './actuacionForm';

const addressAdapter = getAddressAdapter(ADDRESS_AUTOCOMPLETE_PROVIDER, {
  key: ADDRESS_AUTOCOMPLETE_KEY,
});

// NuevaDiligenciaForm — la creación / edición de una diligencia como formulario
// canónico (vive embebido en NuevaDiligenciaModal; dueño de su propio estado,
// delega la mutación al padre vía onSubmit).
//
//   mode="create" → identifica una causa seguida (picker por ROL) + los datos de
//     notificación; dos salidas: Guardar borrador (send=false) / Solicitar
//     cotizaciones (send=true, irreversible).
//   mode="edit"   → la causa queda fija (lockedCase); edita la notificación de un
//     DRAFT y guarda (send=false).
//
// onSubmit(payload, send): el padre corre createDiligenciaByCorte / updateTask.

// Fecha mínima como límite: hoy vale SOLO antes de las 12:00; desde el mediodía
// el mínimo pasa a mañana. Devuelve "YYYY-MM-DD" (hora local).
function minDeadlineDate() {
  const base = new Date();
  if (base.getHours() >= 12) base.setDate(base.getDate() + 1);
  const y = base.getFullYear();
  const m = String(base.getMonth() + 1).padStart(2, '0');
  const d = String(base.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function toDateInput(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return toISODate(d);
}

export default function NuevaDiligenciaForm({
  mode = 'create',
  lockedCase = null,
  initial = {},
  isSubmitting = false,
  apiError,
  onSubmit,
  onCancel,
}) {
  const isEdit = mode === 'edit';

  // ── notification field state (init from `initial`) ──
  const [comunaRegion, setComunaRegion] = useState(
    [initial.comuna, initial.region].filter(Boolean).join(' · '),
  );
  const [address, setAddress] = useState(initial.address || '');
  const [detalle, setDetalle] = useState(initial.detalle || '');
  const [deadline, setDeadline] = useState(toDateInput(initial.deadline));
  const [notas, setNotas] = useState(initial.notes || '');
  // Lo que se pide (REC-1966): actuación, inscripción, destinatarios y tipo
  // de domicilio. Un borrador anterior trae todo vacío.
  const [actuacion, setActuacion] = useState(() => actuacionFromTask(initial));

  const minDeadline = useMemo(() => minDeadlineDate(), []);

  // ── causa picker (create only, unless a causa is locked) ──
  const needsCausaPicker = !isEdit && !lockedCase;
  const [causaQuery, setCausaQuery] = useState(lockedCase?.rol || '');
  const [causaOpen, setCausaOpen] = useState(false);
  const [debouncedCausa, setDebouncedCausa] = useState('');
  const [pickedCase, setPickedCase] = useState(lockedCase || null);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedCausa(causaQuery.trim()), 250);
    return () => clearTimeout(id);
  }, [causaQuery]);

  const causaHasQuery = debouncedCausa.length >= 2;
  const {
    data: causaData,
    isFetching: causaSearching,
    isError: causaSearchFailed,
  } = useCasesPick(debouncedCausa, {
    enabled: needsCausaPicker && !pickedCase,
  });
  // Este es el guardia de «una diligencia activa por causa». Si su lectura cae,
  // el conjunto queda vacío, ninguna causa se filtra, y una que ya tiene
  // diligencia en curso se ofrece como disponible: el formulario se envía y el
  // API lo rechaza. Un guardia que desaparece en silencio es peor que uno que
  // no existe, porque la pantalla afirma que la causa está libre.
  const { data: activeDilData, isError: activeDilFailed } =
    useActiveDiligencias(undefined, {
      enabled: needsCausaPicker && !pickedCase,
    });
  const runningCaseIds = useMemo(() => {
    const list = Array.isArray(activeDilData)
      ? activeDilData
      : (activeDilData?.results ?? []);
    return new Set(list.map((t) => t.caseId).filter(Boolean));
  }, [activeDilData]);
  const causaMatches = useMemo(() => {
    // Sin el guardia no se ofrece nada: elegir una causa cuyo estado no se pudo
    // leer es la vía al envío que el API rechaza.
    if (activeDilFailed) return [];
    const list = Array.isArray(causaData?.results) ? causaData.results : [];
    return list.filter(
      (c) =>
        // El API gatea sobre lo mismo: una causa que el PJUD no confirmó no
        // admite diligencia. Ofrecerla acá era ofrecer un conflicto, y el
        // estado ya viaja en cada fila del listado.
        admitsDiligencia(c.verificationStatus) &&
        !runningCaseIds.has(c.caseId ?? c.id),
    );
  }, [activeDilFailed, causaData, runningCaseIds]);

  function pickCausa(c) {
    setPickedCase({
      caratula: c.title ?? c.caratula ?? null,
      caseId: c.caseId ?? c.id,
      courtName: c.courtName ?? null,
      rol: c.rol ?? '',
      tribunalName: c.tribunalName ?? null,
    });
    setCausaQuery(c.rol ?? '');
    setCausaOpen(false);
  }

  // ── comuna · región: las recientes ──
  const { data: recentTasksData } = useRecentTasks();
  const recentComunas = useMemo(() => {
    const list = Array.isArray(recentTasksData?.results)
      ? [...recentTasksData.results].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        )
      : [];
    // Cada reciente se lleva a su comuna del catálogo actual: una diligencia
    // antigua puede traer la región con otro nombre ("Metropolitana"), y elegirla
    // dejaba un texto que la validación rechaza. Lo que no calza se descarta.
    const seen = new Set();
    const out = [];
    for (const t of list) {
      if (!t.commune) continue;
      const match =
        resolveComuna(`${t.commune} · ${t.region ?? ''}`) ??
        resolveComuna(t.commune);
      if (!match) continue;
      const key = `${match.comuna}|${match.region}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(match);
      if (out.length >= 8) break;
    }
    return out;
  }, [recentTasksData]);

  // Una causa ya guardada puede traer una comuna/región que no calza con la
  // lista; mientras el texto no cambie se conserva tal cual.
  const initialComunaText = [initial.comuna, initial.region]
    .filter(Boolean)
    .join(' · ');
  const resolvedComuna = useMemo(() => {
    const fromList = resolveComuna(comunaRegion);
    if (fromList) return fromList;
    if (initial.comuna && initial.region && comunaRegion === initialComunaText)
      return { comuna: initial.comuna, region: initial.region };
    return null;
  }, [comunaRegion, initial.comuna, initial.region, initialComunaText]);

  // ── validation ──
  const errors = useMemo(() => {
    const e = {};
    if (!deadline) e.deadline = 'Indica la fecha límite.';
    else if (!isValidDateInput(deadline))
      e.deadline = 'Ingresa una fecha válida, con año de 4 dígitos.';
    else if (deadline < minDeadline)
      e.deadline =
        'La fecha límite ya no es válida. Elige hoy (antes de las 12:00) o una fecha futura.';
    if (!comunaRegion.trim()) e.comunaRegion = 'Indica la comuna y la región.';
    else if (!resolvedComuna) e.comunaRegion = 'Elige una comuna de la lista.';
    if (!address.trim()) e.address = 'Indica la dirección.';
    if (needsCausaPicker && !pickedCase)
      e.causa = 'Busca y elige una causa seguida.';
    return { ...e, ...validateActuacion(actuacion, resolveComuna) };
  }, [
    actuacion,
    deadline,
    comunaRegion,
    resolvedComuna,
    address,
    minDeadline,
    needsCausaPicker,
    pickedCase,
  ]);

  const isValid = Object.keys(errors).length === 0;
  const [submitted, setSubmitted] = useState(false);

  function buildPayload() {
    return {
      caseId:
        pickedCase?.caseId ?? lockedCase?.caseId ?? initial.caseId ?? null,
      commune: resolvedComuna.comuna,
      region: resolvedComuna.region,
      address,
      addressDetails: detalle || null,
      deadline: deadline ? new Date(deadline).toISOString() : null,
      notes: notas || null,
      ...actuacionPayload(actuacion, resolveComuna),
    };
  }

  function submit(send) {
    if (isSubmitting) return;
    setSubmitted(true);
    if (!isValid) return;
    onSubmit?.(buildPayload(), send, pickedCase || lockedCase || {});
  }

  const causa = pickedCase || lockedCase;

  const formInner = (
    <form onSubmit={(e) => e.preventDefault()} noValidate>
      {/* Causa — picker (create) o fija (edit / lockedCase) */}
      {needsCausaPicker ? (
        <InputRow label="Causa">
          <div className="ndf-combo">
            <Input
              variant="mono"
              autoComplete="off"
              placeholder="Busca por ROL, por ejemplo C-123-2026"
              value={causaQuery}
              error={submitted && Boolean(errors.causa)}
              onChange={(e) => {
                setCausaQuery(e.target.value);
                setPickedCase(null);
                setCausaOpen(true);
              }}
              onFocus={() => setCausaOpen(true)}
              onBlur={() => setTimeout(() => setCausaOpen(false), 150)}
            />
            {causaOpen && !pickedCase && (
              <div className="ndf-menu">
                {!causaHasQuery && (
                  <div className="ndf-menu-head">
                    Causas seguidas disponibles
                  </div>
                )}
                {causaMatches.length > 0 ? (
                  causaMatches.map((c) => (
                    <button
                      type="button"
                      className="ndf-opt"
                      key={c.caseId ?? c.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pickCausa(c);
                      }}
                    >
                      <b>{c.rol}</b>
                      {c.title ? (
                        <span className="ndf-opt-sub">{c.title}</span>
                      ) : null}
                      <span className="ndf-opt-reg">
                        {[c.courtName, c.tribunalName]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="ndf-opt-empty">
                    {causaSearching
                      ? 'Buscando…'
                      : activeDilFailed
                        ? 'No se pudo comprobar qué causas ya tienen una diligencia en curso. Reintenta en unos segundos.'
                        : causaSearchFailed
                          ? 'No se pudo buscar. Reintenta en unos segundos.'
                          : causaHasQuery
                            ? 'Sin causas seguidas con ese ROL.'
                            : 'No hay causas seguidas disponibles.'}
                  </div>
                )}
              </div>
            )}
          </div>
          {submitted && errors.causa && (
            <HelperText kind="err">{errors.causa}</HelperText>
          )}
        </InputRow>
      ) : (
        <InputRow label="Causa">
          <div className="ndf-fixed mono">
            <b>{causa?.rol || '—'}</b>
            {causa?.caratula ? ` · ${causa.caratula}` : ''}
          </div>
        </InputRow>
      )}

      <InputRow label="Corte">
        <div className="ndf-fixed">{causa?.courtName || '—'}</div>
      </InputRow>
      <InputRow label="Tribunal">
        <div className="ndf-fixed">{causa?.tribunalName || '—'}</div>
      </InputRow>

      <h3 className="ndf-section">Qué se pide</h3>
      <ActuacionFields
        value={actuacion}
        onChange={setActuacion}
        errors={errors}
        showErrors={submitted}
        recentComunas={recentComunas}
      />

      <h3 className="ndf-section">Dónde y plazo</h3>
      <InputRow label="Comuna · Región">
        <ComunaCombo
          value={comunaRegion}
          recent={recentComunas}
          error={submitted && Boolean(errors.comunaRegion)}
          onChange={setComunaRegion}
        />
        {submitted && errors.comunaRegion && (
          <HelperText kind="err">{errors.comunaRegion}</HelperText>
        )}
      </InputRow>

      <InputRow label="Dirección">
        {addressAdapter ? (
          <AddressAutocomplete
            adapter={addressAdapter}
            value={address}
            error={submitted && Boolean(errors.address)}
            onChange={setAddress}
            onSelect={(suggestion) => {
              setComunaRegion((actual) => {
                const match = proposeComuna(suggestion, actual, COMUNAS);
                return match ? `${match.comuna} · ${match.region}` : actual;
              });
            }}
          />
        ) : (
          <Input
            placeholder="Av. Apoquindo 4501"
            value={address}
            error={submitted && Boolean(errors.address)}
            onChange={(e) => setAddress(e.target.value)}
          />
        )}
        {submitted && errors.address && (
          <HelperText kind="err">{errors.address}</HelperText>
        )}
      </InputRow>

      <InputRow label="Detalle">
        <Input
          placeholder="Oficina 802 / depto / piso"
          value={detalle}
          onChange={(e) => setDetalle(e.target.value)}
        />
      </InputRow>

      <div className="ndf-recipient-row">
        <span>Tipo de domicilio</span>
        <AddressTypePicker
          label="Tipo de domicilio"
          value={actuacion.addressType}
          onChange={(addressType) =>
            setActuacion((a) => ({ ...a, addressType }))
          }
        />
      </div>

      <InputRow label="Fecha límite">
        <Input
          variant="mono"
          type="date"
          min={minDeadline}
          max={MAX_DATE_INPUT}
          value={deadline}
          error={submitted && Boolean(errors.deadline)}
          onChange={(e) => setDeadline(e.target.value)}
        />
        {submitted && errors.deadline && (
          <HelperText kind="err">{errors.deadline}</HelperText>
        )}
      </InputRow>

      <InputRow
        label="Notas para el receptor"
        helper={
          <HelperText>
            Cuéntale al receptor qué necesitas y cómo hacer la diligencia.
          </HelperText>
        }
      >
        <Textarea
          rows={6}
          className="ndf-notas"
          placeholder="Ej.: preguntar por Juan Pérez en recepción, piso 8, de 9:00 a 13:00."
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
        />
      </InputRow>

      {apiError && <HelperText kind="err">{apiError}</HelperText>}

      <div className="ndf-actions">
        <Button
          variant="ghost"
          size="md"
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        {isEdit ? (
          <Button
            variant="seal"
            size="md"
            type="button"
            onClick={() => submit(false)}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Guardando…' : 'Guardar'}
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              size="md"
              type="button"
              onClick={() => submit(false)}
              disabled={isSubmitting}
            >
              Guardar borrador
            </Button>
            <Button
              variant="seal"
              size="md"
              type="button"
              onClick={() => submit(true)}
              disabled={isSubmitting}
              title="Envío irreversible: los datos quedan fijos."
            >
              {isSubmitting ? 'Enviando…' : 'Solicitar cotizaciones'}
            </Button>
          </>
        )}
      </div>
    </form>
  );

  return formInner;
}

NuevaDiligenciaForm.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']),
  lockedCase: PropTypes.shape({
    caseId: PropTypes.string,
    rol: PropTypes.string,
    caratula: PropTypes.string,
    courtName: PropTypes.string,
    tribunalName: PropTypes.string,
  }),
  initial: PropTypes.object,
  isSubmitting: PropTypes.bool,
  apiError: PropTypes.string,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};
